import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import Logo from "@/components/landing/Logo";

export default function CustomerLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithPassword, registerWithPassword, startGoogleLogin } = useAuth();
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);

  const next = new URLSearchParams(location.search).get("next") || "/account";

  const fmtErr = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || "").join(" ");
    return "Something went wrong";
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await loginWithPassword(email, password);
        toast.success("Welcome back");
      } else {
        await registerWithPassword(email, password, name);
        toast.success("Account created — 10% off your first detail unlocked!");
      }
      navigate(next, { replace: true });
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center gap-3 mb-6">
          <Logo size={64} />
          <h1 className="font-['Outfit'] text-3xl mt-2">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="text-sm text-gray-500">
            {mode === "signup"
              ? "Save your cars, history, and unlock 10% off"
              : "Sign in to view your bookings"}
          </p>
        </div>

        {/* Toggle */}
        <div className="flex rounded-lg bg-white/[0.03] border border-white/10 p-1 mb-5">
          <button
            data-testid="login-tab-signin"
            onClick={() => setMode("login")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === "login" ? "bg-sky-500 text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            Sign in
          </button>
          <button
            data-testid="login-tab-signup"
            onClick={() => setMode("signup")}
            className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
              mode === "signup" ? "bg-sky-500 text-white" : "text-gray-400 hover:text-white"
            }`}
          >
            Create account
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3" data-testid="login-form">
          <AnimatePresence>
            {mode === "signup" && (
              <motion.div
                key="name"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                style={{ overflow: "hidden" }}
              >
                <Input
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  data-testid="login-name"
                  className="bg-white/[0.03] border-white/10 text-white h-12"
                />
              </motion.div>
            )}
          </AnimatePresence>

          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            data-testid="login-email"
            className="bg-white/[0.03] border-white/10 text-white h-12"
          />
          <Input
            type="password"
            placeholder={mode === "signup" ? "Password (min 6 chars)" : "Password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
            data-testid="login-password"
            className="bg-white/[0.03] border-white/10 text-white h-12"
          />
          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-12 bg-sky-500 hover:bg-sky-400 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : mode === "login" ? (
              <>
                <Mail className="w-4 h-4 mr-2" /> Sign in
              </>
            ) : (
              "Create account & save 10%"
            )}
          </Button>
        </form>

        {/* Divider */}
        <div className="flex items-center gap-3 my-5">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-xs text-gray-500 uppercase tracking-widest">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <Button
          variant="outline"
          onClick={() => startGoogleLogin(next)}
          data-testid="login-google"
          className="w-full h-12 border-white/15 text-white bg-white/[0.03] hover:bg-white/[0.06] hover:text-white"
        >
          <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
            <path fill="#fff" d="M21.35 11.1H12v3.8h5.35c-.23 1.5-1.84 4.4-5.35 4.4-3.22 0-5.85-2.66-5.85-5.95s2.63-5.95 5.85-5.95c1.84 0 3.07.78 3.77 1.45l2.57-2.48C16.96 4.9 14.7 4 12 4 6.92 4 2.85 8.07 2.85 13.35S6.92 22.7 12 22.7c6.93 0 9.5-4.86 9.5-9.4 0-.6-.07-1.1-.15-1.62z" />
          </svg>
          Continue with Google
        </Button>

        <button
          type="button"
          onClick={() => navigate("/")}
          className="block w-full text-center text-sm text-gray-500 hover:text-white mt-5"
        >
          ← Back to site
        </button>
        <button
          type="button"
          data-testid="link-admin-login"
          onClick={() => navigate("/admin/login")}
          className="block w-full text-center text-xs text-gray-600 hover:text-sky-400 mt-3 uppercase tracking-widest"
        >
          Reflections team? Admin login →
        </button>
      </motion.div>
    </div>
  );
}
